import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import '../App.css';

function MonitoringTrends() {
  const { darkMode } = useTheme();
  const [trendsData, setTrendsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('24h');
  const [selectedConfig, setSelectedConfig] = useState('all');

  useEffect(() => {
    fetchTrends();
  }, [period, selectedConfig]);

  const fetchTrends = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const configParam = selectedConfig !== 'all' ? `&configId=${selectedConfig}` : '';
      const response = await fetch(`/api/dashboard/monitoring-trends?period=${period}${configParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch trends data');
      }

      const data = await response.json();
      setTrendsData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    if (period === '24h') {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (period === '7d') {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const formatChartData = (data) => {
    return data.map(item => ({
      time: formatTimestamp(item.timestamp),
      fullTime: new Date(item.timestamp).toLocaleString(),
      healthy: item.healthy,
      warning: item.warning,
      error: item.error,
      total: item.totalChecks,
      avgResponseTime: item.avgResponseTime,
    }));
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: darkMode ? '#1f2937' : 'white',
          border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: darkMode ? '#fff' : '#111' }}>
            {payload[0]?.payload?.fullTime}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: '4px 0', color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="page-container">
      <div className="content-header" style={{ marginBottom: '20px' }}>
        <h1>Monitoring Trends</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select
            value={selectedConfig}
            onChange={(e) => setSelectedConfig(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: darkMode ? '1px solid #374151' : '1px solid #d1d5db',
              background: darkMode ? '#1f2937' : 'white',
              color: darkMode ? '#fff' : '#111',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Monitors</option>
            {trendsData?.configs?.map(config => (
              <option key={config.id} value={config.id}>
                {config.config_name} ({config.object_type})
              </option>
            ))}
          </select>

          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: darkMode ? '1px solid #374151' : '1px solid #d1d5db',
              background: darkMode ? '#1f2937' : 'white',
              color: darkMode ? '#fff' : '#111',
              cursor: 'pointer'
            }}
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>

          <button onClick={fetchTrends} className="btn btn-primary">
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading" style={{ padding: '40px', textAlign: 'center' }}>
          Loading trends data...
        </div>
      ) : error ? (
        <div className="card" style={{ padding: '20px', textAlign: 'center', color: '#ef4444' }}>
          Error: {error}
        </div>
      ) : trendsData ? (
        <>
          {/* Summary Stats */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#6b7280', marginBottom: '8px' }}>
                    Uptime
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: '#22c55e' }}>
                    {trendsData.uptimePercentage}%
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#6b7280', marginBottom: '8px' }}>
                    Total Checks
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: darkMode ? '#fff' : '#111' }}>
                    {trendsData.totalChecks}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#6b7280', marginBottom: '8px' }}>
                    Period
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: darkMode ? '#fff' : '#111' }}>
                    {period === '24h' ? '24h' : period === '7d' ? '7d' : '30d'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Status Distribution Chart */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <div className="card-header">
              <h3 style={{ margin: 0 }}>Check Status Over Time</h3>
            </div>
            <div className="card-body">
              {trendsData.data.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: darkMode ? '#9ca3af' : '#6b7280' }}>
                  No check data available for this period
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={formatChartData(trendsData.data)}>
                    <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                    <XAxis
                      dataKey="time"
                      stroke={darkMode ? '#9ca3af' : '#6b7280'}
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis
                      stroke={darkMode ? '#9ca3af' : '#6b7280'}
                      style={{ fontSize: '12px' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="error"
                      stackId="1"
                      stroke="#ef4444"
                      fill="#ef4444"
                      name="Error"
                    />
                    <Area
                      type="monotone"
                      dataKey="warning"
                      stackId="1"
                      stroke="#f59e0b"
                      fill="#f59e0b"
                      name="Warning"
                    />
                    <Area
                      type="monotone"
                      dataKey="healthy"
                      stackId="1"
                      stroke="#22c55e"
                      fill="#22c55e"
                      name="Healthy"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Response Time Chart */}
          <div className="card">
            <div className="card-header">
              <h3 style={{ margin: 0 }}>Average Response Time</h3>
            </div>
            <div className="card-body">
              {trendsData.data.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: darkMode ? '#9ca3af' : '#6b7280' }}>
                  No response time data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={formatChartData(trendsData.data)}>
                    <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                    <XAxis
                      dataKey="time"
                      stroke={darkMode ? '#9ca3af' : '#6b7280'}
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis
                      stroke={darkMode ? '#9ca3af' : '#6b7280'}
                      style={{ fontSize: '12px' }}
                      label={{ value: 'ms', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="avgResponseTime"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={false}
                      name="Avg Response Time (ms)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

export default MonitoringTrends;
